/**
 * Close Ticket Modal Component
 * @description 티켓 닫기 모달
 */

import { useState } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, Label, Textarea
} from '@shared/ui/primitives';
import { CheckCircle } from 'lucide-react';

interface CloseTicketModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (resolution: string) => Promise<void>;
    ticketId: string;
}

export const CloseTicketModal: React.FC<CloseTicketModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    ticketId,
}) => {
    const [resolution, setResolution] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (!resolution.trim()) {
            return;
        }

        try {
            setIsLoading(true);
            await onConfirm(resolution.trim());
            setResolution('');
            onClose();
        } catch (error) {
            console.error('Close ticket failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setResolution('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        티켓 닫기
                    </DialogTitle>
                    <DialogDescription>
                        티켓을 완료 처리합니다.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                            티켓 ID: <span className="font-mono">{ticketId}</span>
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="resolution">
                            해결 내용 <span className="text-danger">*</span>
                        </Label>
                        <Textarea
                            id="resolution"
                            value={resolution}
                            onChange={(e) => setResolution(e.target.value)}
                            placeholder="티켓 해결 내용을 입력하세요"
                            rows={6}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || !resolution.trim()}
                    >
                        {isLoading ? '처리 중...' : '완료'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

CloseTicketModal.displayName = 'CloseTicketModal';
